import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, ArrowRight, Loader2, Eye, EyeOff } from 'lucide-react';

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn } = useAuth();
  const { t } = useTranslation();

  const from = location.state?.from?.pathname || '/dashboard';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email || !password) {
      setError('Email and password are required.');
      return;
    }

    setSubmitting(true);
    const { error: signInError } = await signIn({ email: email.trim(), password });
    setSubmitting(false);

    if (signInError) {
      const msg = (signInError.message || '').toLowerCase();
      if (msg.includes('invalid login credentials') || msg.includes('invalid')) {
        setError('Invalid email or password. Try again.');
      } else if (msg.includes('email not confirmed')) {
        setError('Email not confirmed. Please confirm via email before signing in.');
      } else {
        setError(signInError.message || 'Unable to sign in. Please try again.');
      }
      return;
    }

    navigate(from, { replace: true });
  };

  return (
    <div
      className="relative min-h-screen w-full overflow-hidden bg-background grain-overlay"
      data-testid="login-page"
    >
      {/* Decorative gold rules - top + bottom */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[hsl(var(--primary))]/40 to-transparent" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-[hsl(var(--primary))]/30 to-transparent" />

      <div className="relative z-10 flex min-h-screen items-center justify-center px-4 py-10">
        <div className="w-full max-w-md">
          {/* Brand hero — composed dark circuit banner (full lockup baked in) */}
          <div className="mb-6 flex flex-col items-center text-center space-y-3">
            <img
              src="/kocore-logo.png"
              alt="K.O. CO+RE Systems — Powered by PQ Intelligence"
              className="h-40 w-auto"
              data-testid="login-brand-logo"
            />
            <p className="text-sm text-muted-foreground max-w-xs">
              {t('auth.login_subtitle')}
            </p>
          </div>

          <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))] shadow-2xl shadow-black/40">
            <CardContent className="p-6 sm:p-8">
              <form onSubmit={handleSubmit} className="space-y-5" noValidate>
                <div className="space-y-2">
                  <Label
                    htmlFor="email"
                    className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground"
                  >
                    {t('auth.email')}
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    placeholder="name@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    aria-invalid={!!error}
                    className="h-11 bg-[hsl(var(--muted))] border-border text-foreground placeholder:text-muted-foreground/60 hover:border-[hsl(var(--primary))]/30 focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]/40 focus-visible:border-[hsl(var(--primary))]/40 transition-colors"
                    data-testid="login-email-input"
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label
                      htmlFor="password"
                      className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground"
                    >
                      {t('auth.password')}
                    </Label>
                    <Link
                      to="/forgot-password"
                      className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground hover:text-[hsl(var(--primary))] transition-colors focus-visible:outline-none focus-visible:text-[hsl(var(--primary))]"
                      data-testid="login-forgot-password-link"
                    >
                      {t('auth.forgot_password_link')}
                    </Link>
                  </div>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      aria-invalid={!!error}
                      className="h-11 pr-11 bg-[hsl(var(--muted))] border-border text-foreground placeholder:text-muted-foreground/60 hover:border-[hsl(var(--primary))]/30 focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]/40 focus-visible:border-[hsl(var(--primary))]/40 transition-colors"
                      data-testid="login-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label={showPassword ? t('auth.hide_password') : t('auth.show_password')}
                      aria-pressed={showPassword}
                      tabIndex={0}
                      className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--primary))]/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]/40 transition-colors"
                      data-testid="login-password-toggle"
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" strokeWidth={2} />
                      ) : (
                        <Eye className="h-4 w-4" strokeWidth={2} />
                      )}
                    </button>
                  </div>
                </div>

                {error ? (
                  <Alert
                    variant="default"
                    className="border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/10 text-foreground"
                    data-testid="login-error-message"
                  >
                    <AlertCircle className="h-4 w-4 text-[hsl(var(--destructive))]" />
                    <AlertDescription className="text-sm">{error}</AlertDescription>
                  </Alert>
                ) : null}

                <Button
                  type="submit"
                  disabled={submitting}
                  className="group relative w-full h-11 bg-primary text-primary-foreground hover:bg-primary/90 active:bg-primary/85 active:scale-[0.99] focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]/40 transition-colors font-semibold tracking-[0.16em] uppercase"
                  data-testid="login-submit-button"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {t('auth.logging_in')}
                    </>
                  ) : (
                    <>
                      {t('auth.sign_in')}
                      <ArrowRight className="h-4 w-4 ml-2 transition-transform group-hover:translate-x-0.5" />
                    </>
                  )}
                </Button>

                <div className="text-center text-xs text-muted-foreground pt-1">
                  New here?{' '}
                  <Link
                    to="/signup"
                    className="text-[hsl(var(--primary))] underline hover:text-[hsl(var(--primary))]/80"
                    data-testid="login-signup-link"
                  >
                    Get founding access
                  </Link>
                </div>
              </form>
            </CardContent>
          </Card>

          <p className="mt-6 text-center text-[11px] uppercase tracking-[0.28em] text-muted-foreground/70">
            © K.O. CO+RE SYSTEMS · Secure Terminal
          </p>
          <div
            className="mt-3 flex items-center justify-center gap-2 text-[10px] uppercase tracking-[0.28em] text-muted-foreground/60"
            data-testid="login-powered-by-pq"
          >
            <img
              src="/pq-intelligence-logo.png"
              alt=""
              aria-hidden="true"
              className="h-4 w-auto shrink-0 opacity-80"
            />
            <span>{t('brand.powered_by')}</span>
            <img
              src="/brand/pq-wordmark.webp"
              alt="PQ Intelligence"
              className="h-3 w-auto select-none"
              draggable={false}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
